import { APIEndpoints } from '@/@admin/types/constants';
import { AdminToolsParams, Dashboard, UpdateResponse } from '@/@admin/types/entities';
import { buildCacheKey } from '@/@admin/utils';
import { ERROR_MESSAGES } from '@/@cms/types';
import { tracking } from '@/lib/features/tracking';
import { portalApi } from '@/lib/http/axios';
import { BaseService } from './base.service';

/**
 * Service class for dashboard management
 */
export class DashboardService extends BaseService {
    constructor() {
        super(portalApi);
    }

    async getDashboards(params: AdminToolsParams): Promise<Dashboard[]> {
        try {
            const cacheKey = buildCacheKey('adminFetchDashboards', params);
            const cachedData = this.getCachedData(cacheKey);
            if (cachedData) return cachedData;
            let dashboards: Dashboard[] = [];

            const { data: response } = await this.axiosInstance.get<{
                hasError: boolean;
                message?: string;
                data?: Dashboard[];
            }>(`${APIEndpoints.DASHBOARDS}/all`, {
                params,
            });

            if (response?.data) {
                dashboards = response.data;
            }

            if (response?.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'No dashboards data received from the server'),
                    'studio.DashboardService.getDashboards',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        response.message || 'No dashboards data received from the server',
                    ),
                );
            }

            // Cache the response data
            this.setCachedData(cacheKey, dashboards);
            return dashboards;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to get dashboards: ${errorMessage}`),
                'studio.DashboardService.getDashboards',
            );
            throw new Error(ERROR_MESSAGES.API_ERROR(`Failed to get dashboards: ${errorMessage}`));
        }
    }

    async updateDashboard(dashboardId: number, data: Partial<Dashboard>): Promise<UpdateResponse> {
        try {
            const { data: response } = await this.axiosInstance.patch<UpdateResponse>(
                `${APIEndpoints.DASHBOARDS}/${dashboardId}`,
                data,
            );

            // Ensure we have a valid response
            if (response?.hasError) {
                tracking.errorWithSource(
                    new Error(response.message || 'No dashboard data received from the server'),
                    'studio.DashboardService.updateDashboard',
                );
                throw new Error(
                    ERROR_MESSAGES.API_ERROR(
                        response.message || 'No dashboard data received from the server',
                    ),
                );
            }

            this.clearCache();
            return response;
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            tracking.errorWithSource(
                new Error(`Failed to update dashboard: ${errorMessage}`),
                'studio.DashboardService.updateDashboard',
            );
            throw new Error(
                ERROR_MESSAGES.API_ERROR(`Failed to update dashboard: ${errorMessage}`),
            );
        }
    }
}

export const dashboardService = new DashboardService();
